import { useCallback, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import type { BaseStoredNote, BaseStatusMessage } from "../types";
import { EVM_CONFIG, type TokenInfo } from "../../config";
import { flatRelayFee } from "../../joinsplit-v2/relayFee";
import { friendlyError } from "../utils/errors";
import { parsePrivateAddress } from "../../utils/inbox-crypto";
import {
  isStealthHandle,
  resolveStealthHandle,
} from "../../utils/handle-resolver";
import {
  executeFlatJoinSplitV2Transfer,
  executeFlatJoinSplitV2Withdraw,
  executeFlatJoinSplitV2BatchWithdraw,
  executeFlatJoinSplitV2Migration,
} from "../../joinsplit-v2/operations";
import { disposeFlatJoinSplitV2Prover } from "../../joinsplit-v2/prover";
import { withFlatJoinSplitV2WalletLock } from "../../joinsplit-v2/walletLock";
import { migrateRetiredBalance } from "../helpers/migrateRetiredBalance";
import {
  privatePoolForToken,
  privateSpendSource,
} from "../../joinsplit-v2/privatePools";
import { usesPrivateFees } from "../../joinsplit-v2/privateFees";

interface UseBaseSendOperationParams {
  signer: ethers.Signer | null;
  spendingKeys: any;
  notes: BaseStoredNote[];
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
  setStatus: (status: BaseStatusMessage | null) => void;
  /** The relayer fee for an amount on this network: the live quote's on
   * quoted networks (null while none is fresh), the flat Base fee otherwise. */
  relayFeeFor?: ((amount: bigint) => bigint) | null;
}

type SendOperationResult = {
  mode: null | "transfer" | "withdraw" | "migration";
  txHash?: string;
  spentCommitments?: string[];
  changeNote?: BaseStoredNote["note"];
  recipientNote?: BaseStoredNote["note"];
  recipientMpkHex?: string;
  recipientInboxPublicKeyHex?: string;
  journalIds?: string[];
  migrationNotes?: BaseStoredNote[];
};

export type BatchWithdrawRecipient = {
  recipient: string;
  amountStr: string;
};

type BatchWithdrawOperationResult = {
  mode: null | "withdraw_batch" | "transfer_batch";
  txHash?: string;
  spentCommitments?: string[];
  changeNote?: BaseStoredNote["note"];
  recipientNotes?: Array<{
    note: BaseStoredNote["note"];
    recipientMpkHex: string;
    recipientInboxPublicKeyHex: string;
    amountStr: string;
    rawRecipient: string;
  }>;
};

export function useBaseSendOperation({
  signer,
  spendingKeys,
  notes,
  addLog: appendLog,
  setStatus,
  relayFeeFor = flatRelayFee,
}: UseBaseSendOperationParams) {
  // The fee is bound into the proof: without a fresh quote there is no fee
  // to bind, so the operation refuses instead of guessing one.
  const boundRelayFee = useCallback(
    (amount: bigint): bigint => {
      if (!relayFeeFor)
        throw new Error(
          "The network fee is still being quoted. Try again in a moment.",
        );
      return relayFeeFor(amount);
    },
    [relayFeeFor],
  );
  const activeOperationRef = useRef<AbortController | null>(null);
  const [sendProgress, setSendProgress] = useState("");
  const addLog = useCallback<UseBaseSendOperationParams["addLog"]>(
    (message, type, hash) => {
      appendLog(message, type, hash);
      if (/^Preparing transaction/.test(message)) setSendProgress(message);
      else if (/Checking relayer/.test(message))
        setSendProgress("Checking relayer availability…");
      else if (/Reading deposit history/.test(message))
        setSendProgress("Checking your private balance…");
      else if (/Validating private inputs/.test(message))
        setSendProgress("Checking your private notes…");
      else if (
        /proving key|proving-key|Loading.*proving|Preparing browser proof/.test(
          message,
        )
      )
        setSendProgress("Loading private proof data…");
      else if (/Generating proof locally|Creating private proof/.test(message))
        setSendProgress("Creating your private transaction proof…");
      else if (/Verifying proof locally|Checking private proof/.test(message))
        setSendProgress("Verifying your private transaction proof…");
      else if (/Browser proof ready/.test(message))
        setSendProgress("Finishing private proofs…");
      else if (/Simulating the complete/.test(message))
        setSendProgress("Checking the transaction…");
      else if (
        /Checking accepted fees|Authorizing private settlement|Saving encrypted recovery/.test(
          message,
        )
      )
        setSendProgress(message);
      else if (/Submitting through the relayer/.test(message))
        setSendProgress("Submitting transaction…");
      else if (/Requesting wallet signature/.test(message))
        setSendProgress("Confirm in your wallet…");
      else if (/Settlement submitted/.test(message))
        setSendProgress("Waiting for confirmation…");
    },
    [appendLog],
  );

  useEffect(
    () => () => {
      activeOperationRef.current?.abort();
      activeOperationRef.current = null;
      disposeFlatJoinSplitV2Prover();
    },
    [signer, spendingKeys],
  );

  const handleSend = useCallback(
    async (
      recipient: string,
      amountStr: string,
      token?: TokenInfo,
      privateMemo?: string,
    ): Promise<SendOperationResult> => {
      if (!signer || !spendingKeys) {
        setStatus({ type: "error", message: "Connect wallet first" });
        return { mode: null, txHash: undefined };
      }

      const decimals = token?.decimals ?? 18;
      const symbol = token?.symbol ?? "ETH";
      const selectedTokenType = token?.tokenType ?? "erc20";
      const tokenType =
        selectedTokenType === "vnft"
          ? (token?.wrappedUnderlyingTokenType ?? "erc721")
          : selectedTokenType;
      // For wrapped vNFT notes, proving/selection must use the pool-facing identity
      // (vault address + claimHash-derived subID). Underlying NFT fields are carried
      // separately in wrapped metadata and used at unwrap time.
      const tokenAddress = token?.address ?? ethers.ZeroAddress;
      const tokenSubID = token?.tokenSubID;
      const isWrappedNft = Boolean(
        (selectedTokenType === "vnft" ||
          tokenType === "erc721" ||
          tokenType === "erc1155") &&
        (token?.isWrappedNft ||
          token?.wrappedClaimHash ||
          token?.wrappedUnderlyingTokenAddress),
      );

      const amount = ethers.parseUnits(amountStr || "0", decimals);
      if (amount <= 0n) {
        setStatus({ type: "error", message: "Enter a valid amount" });
        return { mode: null, txHash: undefined };
      }
      if (
        (selectedTokenType === "vnft" ||
          tokenType === "erc721" ||
          tokenType === "erc1155") &&
        !EVM_CONFIG.NFT_EVM_ENABLED
      ) {
        setStatus({
          type: "error",
          message: "NFT send/unshield is disabled in this environment",
        });
        return { mode: null, txHash: undefined };
      }
      // Wrapped vNFTs can represent ERC721 or ERC1155; enforce single-unit only for ERC721 semantics.
      if (tokenType === "erc721" && amount !== 1n) {
        setStatus({
          type: "error",
          message: "ERC721 amount must be exactly 1",
        });
        return { mode: null, txHash: undefined };
      }
      if (
        (selectedTokenType === "vnft" ||
          tokenType === "erc721" ||
          tokenType === "erc1155") &&
        (!tokenSubID || tokenSubID.trim() === "")
      ) {
        setStatus({ type: "error", message: "Missing NFT token ID" });
        return { mode: null, txHash: undefined };
      }

      const trimmed: string = String(recipient).trim();
      const isWithdraw = !trimmed || ethers.isAddress(trimmed);

      try {
        if (selectedTokenType !== "erc20" || tokenType !== "erc20") {
          throw new Error(
            "The fresh JoinSplit pool currently supports fungible ETH/ERC20 notes only",
          );
        }
        if (activeOperationRef.current) {
          throw new Error("A private proof operation is already in progress");
        }
        const controller = new AbortController();
        activeOperationRef.current = controller;
        setSendProgress("Preparing transaction…");
        try {
          let parsedPrivate = parsePrivateAddress(trimmed);
          if (!isWithdraw) {
            if (!parsedPrivate && isStealthHandle(trimmed)) {
              const resolved = await resolveStealthHandle(trimmed, "base", {
                signal: controller.signal,
              });
              if (!resolved.mpkHex) {
                throw new Error(
                  "Resolved handle does not include MPK routing data",
                );
              }
              parsedPrivate = {
                mpkHex: resolved.mpkHex,
                inboxPublicKeyHex: resolved.inboxPublicKeyHex,
              };
            }
            if (!parsedPrivate?.mpkHex || !parsedPrivate.inboxPublicKeyHex) {
              throw new Error(
                "Private recipient must include both an MPK and inbox public key",
              );
            }
          }
          const accountAddress = await signer.getAddress();
          return await withFlatJoinSplitV2WalletLock({
            chainId: EVM_CONFIG.CHAIN_ID,
            poolAddress: EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
            accountAddress,
            signal: controller.signal,
            run: async () => {
              const migrateRetiredNotes = async () => {
                const provider = signer.provider;
                if (!provider) return null;
                const source = privateSpendSource(
                  notes,
                  tokenAddress,
                  amount,
                ).pool;
                const pool = new ethers.Contract(
                  source,
                  ["function currentTreeIndex() view returns (uint256)"],
                  provider,
                );
                return migrateRetiredBalance({
                  notes,
                  amount,
                  token: tokenAddress,
                  chainId: EVM_CONFIG.CHAIN_ID,
                  pool: source,
                  signal: controller.signal,
                  currentTree: async () =>
                    Number(await pool.currentTreeIndex()),
                  migrate: (workingNotes, sourceTreeIndex, commitments) =>
                    executeFlatJoinSplitV2Migration({
                      signer,
                      spendingKeys,
                      notes: workingNotes,
                      tokenAddress,
                      sourceTreeIndex,
                      poolAddress: source,
                      commitments,
                      onLog: addLog,
                      signal: controller.signal,
                    }),
                });
              };

              if (isWithdraw) {
                const withdrawRecipient = trimmed
                  ? ethers.getAddress(trimmed)
                  : ethers.getAddress(await signer.getAddress());
                const relayWithdrawals =
                  EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS;
                // The fee is bound into the proof, so it is fixed here and
                // cannot be renegotiated once proving starts.
                const relayFee = relayWithdrawals ? boundRelayFee(amount) : 0n;
                if (relayWithdrawals && !usesPrivateFees() && relayFee === 0n) {
                  throw new Error(
                    "Relayed withdrawal amount is too small to cover the relayer fee",
                  );
                }
                let result = await executeFlatJoinSplitV2Withdraw({
                  signer,
                  spendingKeys,
                  notes,
                  amount,
                  fee: relayFee,
                  relay: relayWithdrawals,
                  recipient: withdrawRecipient,
                  tokenAddress,
                  tokenDecimals: decimals,
                  onLog: addLog,
                  signal: controller.signal,
                });
                let migrationResult: Awaited<
                  ReturnType<typeof migrateRetiredNotes>
                > = null;
                if (
                  !result.success &&
                  /retired-tree funds require migration|insufficient spendable private balance/i.test(
                    result.error || "",
                  )
                ) {
                  migrationResult = await migrateRetiredNotes();
                  if (
                    migrationResult?.success &&
                    migrationResult.outputNotes[0]
                  ) {
                    const workingNotes = migrationResult.workingNotes;
                    result = await executeFlatJoinSplitV2Withdraw({
                      signer,
                      spendingKeys,
                      notes: workingNotes,
                      amount,
                      fee: relayFee,
                      relay: relayWithdrawals,
                      recipient: withdrawRecipient,
                      tokenAddress,
                      tokenDecimals: decimals,
                      onLog: addLog,
                      signal: controller.signal,
                    });
                  }
                }
                if (!result.success) {
                  if (
                    migrationResult?.success &&
                    migrationResult.outputNotes[0]
                  ) {
                    setStatus({
                      type: "success",
                      message:
                        "Retired notes were migrated. Retry the withdrawal after wallet sync.",
                    });
                    return {
                      mode: "migration",
                      txHash: migrationResult.txHash,
                      spentCommitments: migrationResult.spentNotes.map(
                        (note) => note.commitment,
                      ),
                      changeNote: migrationResult.outputNotes[0],
                      journalIds: migrationResult.journalIds,
                      migrationNotes: migrationResult.migrationNotes,
                    };
                  }
                  throw new Error(
                    result.error || "Fresh-pool withdrawal failed",
                  );
                }
                setSendProgress("Transaction complete");
                setStatus({
                  type: "success",
                  message: `Sent ${amountStr} ${symbol}`,
                  transactionComplete: true,
                });
                return {
                  mode: "withdraw",
                  txHash: result.txHash,
                  spentCommitments: [
                    ...(migrationResult?.spentNotes || []),
                    ...result.spentNotes,
                  ].map((note) => note.commitment),
                  changeNote: result.changeNote,
                  journalIds: [
                    ...(migrationResult?.journalIds || []),
                    result.journalId,
                  ].filter((value): value is string => Boolean(value)),
                  migrationNotes: [
                    ...(migrationResult?.migrationNotes || []),
                    ...(result.gasChangeNote
                      ? [
                          {
                            note: result.gasChangeNote,
                            timestamp: Date.now(),
                            txHash: result.txHash,
                          },
                        ]
                      : []),
                  ],
                };
              }

              let result = await executeFlatJoinSplitV2Transfer({
                signer,
                spendingKeys,
                notes,
                recipientMpk: BigInt(parsedPrivate.mpkHex),
                recipientInboxPublicKeyHex: parsedPrivate.inboxPublicKeyHex,
                recipientPrivateHistory: {
                  type: "receive",
                  amount: amountStr,
                  symbol,
                  memo: privateMemo || undefined,
                  timestamp: Date.now(),
                },
                amount,
                tokenAddress,
                tokenDecimals: decimals,
                onLog: addLog,
                signal: controller.signal,
              });
              let migrationResult: Awaited<
                ReturnType<typeof migrateRetiredNotes>
              > = null;
              if (
                !result.success &&
                /insufficient spendable private balance/i.test(
                  result.error || "",
                )
              ) {
                migrationResult = await migrateRetiredNotes();
                if (
                  migrationResult?.success &&
                  migrationResult.outputNotes[0]
                ) {
                  const workingNotes = migrationResult.workingNotes;
                  result = await executeFlatJoinSplitV2Transfer({
                    signer,
                    spendingKeys,
                    notes: workingNotes,
                    recipientMpk: BigInt(parsedPrivate.mpkHex),
                    recipientInboxPublicKeyHex: parsedPrivate.inboxPublicKeyHex,
                    recipientPrivateHistory: {
                      type: "receive",
                      amount: amountStr,
                      symbol,
                      memo: privateMemo || undefined,
                      timestamp: Date.now(),
                    },
                    amount,
                    tokenAddress,
                    tokenDecimals: decimals,
                    onLog: addLog,
                    signal: controller.signal,
                  });
                }
              }
              if (!result.success) {
                if (
                  migrationResult?.success &&
                  migrationResult.outputNotes[0]
                ) {
                  setStatus({
                    type: "success",
                    message:
                      "Retired notes were migrated. Retry the transfer after wallet sync.",
                  });
                  return {
                    mode: "migration",
                    txHash: migrationResult.txHash,
                    spentCommitments: migrationResult.spentNotes.map(
                      (note) => note.commitment,
                    ),
                    changeNote: migrationResult.outputNotes[0],
                    journalIds: migrationResult.journalIds,
                    migrationNotes: migrationResult.migrationNotes,
                  };
                }
                throw new Error(result.error || "Fresh-pool transfer failed");
              }
              setSendProgress("Transaction complete");
              setStatus({
                type: "success",
                message: `Transferred ${amountStr} ${symbol} privately`,
                transactionComplete: true,
              });
              return {
                mode: "transfer",
                txHash: result.txHash,
                spentCommitments: [
                  ...(migrationResult?.spentNotes || []),
                  ...result.spentNotes,
                ].map((note) => note.commitment),
                recipientNote: result.outputNotes[0],
                changeNote: result.changeNote,
                recipientMpkHex: parsedPrivate.mpkHex,
                recipientInboxPublicKeyHex: parsedPrivate.inboxPublicKeyHex,
                journalIds: [
                  ...(migrationResult?.journalIds || []),
                  result.journalId,
                ].filter((value): value is string => Boolean(value)),
                migrationNotes: [
                  ...(migrationResult?.migrationNotes || []),
                  ...(result.gasChangeNote
                    ? [
                        {
                          note: result.gasChangeNote,
                          timestamp: Date.now(),
                          txHash: result.txHash,
                        },
                      ]
                    : []),
                ],
              };
            },
          });
        } finally {
          if (activeOperationRef.current === controller) {
            activeOperationRef.current = null;
          }
        }
      } catch (error) {
        const message = friendlyError(error);
        setStatus({ type: "error", message });
        addLog(`Send failed: ${message}`, "error");
        return { mode: null, txHash: undefined };
      }
    },
    [signer, spendingKeys, notes, addLog, setStatus, boundRelayFee],
  );

  const handleBatchWithdraw = useCallback(
    async (
      rows: BatchWithdrawRecipient[],
      token?: TokenInfo,
    ): Promise<BatchWithdrawOperationResult> => {
      if (!signer || !spendingKeys) {
        setStatus({ type: "error", message: "Connect wallet first" });
        return { mode: null };
      }

      const decimals = token?.decimals ?? 18;
      const symbol = token?.symbol ?? "ETH";
      const tokenAddress = token?.address ?? ethers.ZeroAddress;

      try {
        if ((token?.tokenType ?? "erc20") !== "erc20") {
          throw new Error(
            "Batch payments support fungible ETH/ERC20 notes only",
          );
        }
        if (rows.length < 2 || rows.length > 32) {
          throw new Error("Batch payments support 2 to 32 recipients");
        }
        const items = rows.map((row, index) => {
          const trimmed = String(row.recipient).trim();
          // Stealth handles and private addresses are the transfer path; one
          // proof paying N public recipients only handles plain addresses.
          if (!ethers.isAddress(trimmed)) {
            throw new Error(
              `Recipient ${index + 1} must be a plain EVM address`,
            );
          }
          const amount = ethers.parseUnits(row.amountStr || "0", decimals);
          if (amount <= 0n) {
            throw new Error(`Recipient ${index + 1} needs a valid amount`);
          }
          return { recipient: ethers.getAddress(trimmed), amount };
        });
        const total = items.reduce((sum, item) => sum + item.amount, 0n);
        const relayWithdrawals = EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS;
        // The fee is bound into the proof, so it is fixed here and cannot be
        // renegotiated once proving starts. Charged on top of the row total.
        const relayFee = relayWithdrawals ? boundRelayFee(total) : 0n;
        if (relayWithdrawals && relayFee === 0n) {
          throw new Error(
            "Relayed batch total is too small to cover the relayer fee",
          );
        }

        if (activeOperationRef.current) {
          throw new Error("A private proof operation is already in progress");
        }
        const controller = new AbortController();
        activeOperationRef.current = controller;
        setSendProgress("Preparing transaction…");
        try {
          const accountAddress = await signer.getAddress();
          return await withFlatJoinSplitV2WalletLock({
            chainId: EVM_CONFIG.CHAIN_ID,
            poolAddress: EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
            accountAddress,
            signal: controller.signal,
            run: async () => {
              const result = await executeFlatJoinSplitV2BatchWithdraw({
                signer,
                spendingKeys,
                notes,
                items,
                fee: relayFee,
                relay: relayWithdrawals,
                tokenAddress,
                onLog: addLog,
                signal: controller.signal,
              });
              if (!result.success) {
                throw new Error(result.error || "Batch payment failed");
              }
              setSendProgress("Transaction complete");
              setStatus({
                type: "success",
                message: `Paid ${items.length} recipients ${symbol}`,
                transactionComplete: true,
              });
              return {
                mode: "withdraw_batch" as const,
                txHash: result.txHash,
                spentCommitments: result.spentNotes.map(
                  (note) => note.commitment,
                ),
                changeNote: result.changeNote,
              };
            },
          });
        } finally {
          if (activeOperationRef.current === controller) {
            activeOperationRef.current = null;
          }
        }
      } catch (error) {
        const message = friendlyError(error);
        setStatus({ type: "error", message });
        addLog(`Batch payment failed: ${message}`, "error");
        return { mode: null };
      }
    },
    [signer, spendingKeys, notes, addLog, setStatus, boundRelayFee],
  );

  return { handleSend, handleBatchWithdraw, sendProgress };
}
